(() => {
	"use strict";

	const selectors = {
		template: "[data-video-template]",
		video: "video",
	};

	const classes = {
		loading: "is-loading",
	};

	const attributes = {
		videoId: "data-video-id",
	};

	const userAgent = (navigator.userAgent || "").toLowerCase();
	const isTikTokInApp = /(tiktok|bytedance|aweme|musical_ly|trill)/.test(userAgent);

	class VideoBackground extends HTMLElement {
		constructor() {
			super();
			this.video = null;
			this.videoTemplateObserver = null;
			this.videoPlayObserver = null;
			this.powerSaverVideoPlay = this.powerSaverVideoPlay.bind(this);
		}

		connectedCallback() {
			this.videoId = this.getAttribute(attributes.videoId);
			this.videoTemplate = this.querySelector(selectors.template);

			if (!this.videoId || !this.videoTemplate) return;

			// TikTok/ByteDance in-app browsers can force native popup/fullscreen
			// for autoplaying videos while scrolling. Keep poster-only there.
			if (isTikTokInApp) {
				this.classList.remove(classes.loading);
				return;
			}

			this.renderVideo();
		}

		enforceInlinePlaybackAttributes(video) {
			if (!video) return;

			video.setAttribute("playsinline", "");
			video.setAttribute("webkit-playsinline", "");
			video.setAttribute("x5-playsinline", "true");
			video.setAttribute("x5-video-player-type", "h5");
			video.setAttribute("x5-video-player-fullscreen", "false");
			video.removeAttribute("controls");
			video.controls = false;
			video.muted = true;
			video.defaultMuted = true;
		}

		playVideo(video) {
			if (!video || typeof video.play !== "function") return;

			const playPromise = video.play();
			if (playPromise !== undefined) {
				playPromise.catch(() => {
					document.addEventListener("click", this.powerSaverVideoPlay, {
						passive: true,
						bubbles: true,
						once: true,
					});

					document.addEventListener("touchstart", this.powerSaverVideoPlay, {
						passive: true,
						bubbles: true,
						once: true,
					});
				});
			}
		}

		powerSaverVideoPlay() {
			if (this.video) this.playVideo(this.video);
		}

		renderVideo() {
			this.videoTemplateObserver = new IntersectionObserver(
				(entries, observer) => {
					entries.forEach((entry) => {
						if (!entry.isIntersecting) return;

						const html = this.videoTemplate.innerHTML;
						this.innerHTML = html;
						this.classList.remove(classes.loading);

						this.video = this.querySelector(selectors.video);
						this.enforceInlinePlaybackAttributes(this.video);
						this.observeVideoPlayToggle();
						this.playVideo(this.video);

						observer.unobserve(entry.target);
					});
				},
				{ root: null, rootMargin: "300px", threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] }
			);

			this.videoTemplateObserver.observe(this);
		}

		observeVideoPlayToggle() {
			if (!this.video) return;

			this.videoPlayObserver = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						if (entry.isIntersecting) {
							this.playVideo(this.video);
							return;
						}

						if (typeof this.video.pause === "function") this.video.pause();
					});
				},
				{ rootMargin: "0px", threshold: [0, 1] }
			);

			this.videoPlayObserver.observe(this.video);
		}

		disconnectedCallback() {
			if (this.videoTemplateObserver) this.videoTemplateObserver.disconnect();
			if (this.videoPlayObserver) this.videoPlayObserver.disconnect();

			document.removeEventListener("click", this.powerSaverVideoPlay);
			document.removeEventListener("touchstart", this.powerSaverVideoPlay);
		}
	}

	if (!customElements.get("video-background")) {
		customElements.define("video-background", VideoBackground);
	}
})();
